import React from "react";
import {
  Box,
  Chip,
  Divider,
  MenuItem,
  Typography,
  Skeleton,
  Button,
  Tooltip,
} from "@mui/material";
import {
  CursorDefaultClick,
  Mail,
  MailOpen,
  MoreHorizontal,
  Trigger,
  Refresh02,
  User,
  UserMinus,
} from "@tiny/icons";
import { Handle, NodeProps, Position } from "reactflow";
import MenuIconButton from "../../../../components/MenuIconButton/MenuIconButton";
import { EmailNodeStats, NodeData } from "../types";
import useDeleteNode from "../Hooks/useDeleteNode";
import { useWorkflowsBuilderPageView } from "../../WorkflowsBuilderPage.context";
import { TruncateTooltip } from "../../../../components";
import { usePutWorkflowsEmailTemplateMutation } from "../../../../app/useGeneratedApi";
import { formatNumber, formatPercent } from "@tiny/utils/formatUtils";
import { useDrawerInvokers } from "@tiny/lib";
import AssignedToAudienceForm from "../../../../customComponents/AssignedToAudienceForm/AssignedToAudienceForm";
import AssignToTagForm from "../../../../customComponents/AssignToTagForm/AssignToTagForm";
import RemoveFromAudienceForm from "../../../../customComponents/RemoveFromAudienceForm/RemoveFromAudienceForm";

const ActionNode = ({ id, data }: NodeProps<NodeData>) => {
  const { deleteNode } = useDeleteNode();
  const { configured } = data.config;
  const {
    openCampaignBuilder,
    viewMode,
    isStatsLoading,
    getWorkflowStatsError,
    refetchWorkflowStats,
    workflowStats,
    showNodeStats,
  } = useWorkflowsBuilderPageView();

  const { showDrawer } = useDrawerInvokers();

  const {
    mutateAsync: putWorkflowMutateAsync,
    isLoading: isPutWorkflowLoading,
  } = usePutWorkflowsEmailTemplateMutation();

  const nodeStats = workflowStats?.[id] as EmailNodeStats | undefined;

  const handleDelete = async (e: React.SyntheticEvent) => {
    e?.stopPropagation();
    try {
      if (data.label === "Email" && configured && data?.template_id) {
        await putWorkflowMutateAsync({
          id: data?.template_id,
          data: { status: "DELETED" },
        });
      }
      deleteNode(id);
    } catch (error) {
      console.error("Failed to delete workflow:", error);
    }
  };

  const handleRefreshStats = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    refetchWorkflowStats();
  };

  const handleOpenForm = (e?: React.SyntheticEvent) => {
    e?.stopPropagation();

    if (data.label === "Email") {
      openCampaignBuilder(id);
    } else if (data.label === "Add To Audience") {
      showDrawer({
        children: <AssignedToAudienceForm id={id} />,
      });
    } else if (data.label === "Add a Tag") {
      showDrawer({
        children: <AssignToTagForm id={id} />,
      });
    } else if (data.label === "Remove From Audience") {
      showDrawer({
        children: <RemoveFromAudienceForm id={id} />,
      });
    }
  };

  const renderStatsContent = () => {
    if (data.label !== "Email") return null;

    if (getWorkflowStatsError) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Typography variant="label2" fontSize={12} color="error">
            Failed to load stats
          </Typography>
          <Button
            size="small"
            startIcon={<Refresh02 sx={{ fontSize: 12 }} />}
            onClick={handleRefreshStats}
            sx={{ minWidth: "auto", fontSize: 12 }}
          >
            Reload
          </Button>
        </Box>
      );
    }

    if (isStatsLoading) {
      return (
        <Box display="flex" gap={3} justifyContent="space-around">
          {[1, 2, 3].map((index) => (
            <Box key={index} display="flex" alignItems="center" gap={1}>
              <Box>
                <Skeleton variant="text" width={60} height={16} />
                <Skeleton variant="text" width={20} height={14} />
              </Box>
            </Box>
          ))}
        </Box>
      );
    }

    const sent = formatNumber(nodeStats?.sent || 0);
    const opened = formatNumber(nodeStats?.opens || 0);
    const clicked = formatNumber(nodeStats?.clicks || 0);
    const openRate = formatPercent(nodeStats?.openRate || 0);
    const clickRate = formatPercent(nodeStats?.clickRate || 0);
    const spamRate = formatPercent(nodeStats?.spamRate || 0);
    const unsubscribeRate = formatPercent(nodeStats?.unsubscribeRate || 0);

    return (
      <Tooltip
        title={`Open rate ${openRate}  | Click rate ${clickRate} | Spam rate ${spamRate} | Unsubscribe rate ${unsubscribeRate}`}
        componentsProps={{
          popper: {
            sx: {
              "& .MuiTooltip-tooltip": {
                padding: 4,
              },
            },
          },
        }}
        arrow
      >
        <Box display="flex" gap={3} justifyContent="space-around">
          <Box display="flex" alignItems="center" gap={1}>
            <Mail sx={{ fontSize: 16, color: "grey.80" }} />
            <Typography variant="caption" color="grey.100" component="div">
              <strong>Sent</strong>
              <Typography color="grey.80" component="span" ml={1}>
                {sent}
              </Typography>
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <MailOpen sx={{ fontSize: 16, color: "grey.80" }} />
            <Typography variant="label2R" color="grey.100" component="div">
              <strong>Opened</strong>
              <Typography color="grey.80" component="span" ml={1}>
                {opened}
              </Typography>
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <CursorDefaultClick sx={{ fontSize: 16, color: "grey.80" }} />
            <Typography variant="caption" color="grey.100" component="div">
              <strong>Clicked</strong>
              <Typography color="grey.80" component="span" ml={1}>
                {clicked}
              </Typography>
            </Typography>
          </Box>
        </Box>
      </Tooltip>
    );
  };

  const icon =
    data.label === "Email" ? (
      <Trigger
        sx={(theme) => ({
          fontSize: 20,
          mr: 1,
          color: theme.palette.purple[150],
        })}
      />
    ) : data.label === "Remove From Audience" ? (
      <UserMinus
        sx={(theme) => ({
          fontSize: 20,
          mr: 1,
          color: theme.palette.red[100],
        })}
      />
    ) : (
      <User
        sx={(theme) => ({
          fontSize: 20,
          mr: 1,
          color: theme.palette.green[150],
        })}
      />
    );

  return (
    <>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: "transparent", border: "none" }}
      />

      <Box
        sx={(theme) => ({
          background: theme.palette.common.white,
          border: `1px solid ${theme.palette.grey[80]}`,
          padding: 3,
          paddingBottom: 4,
          borderRadius: 3,
          display: "flex",
          flexDirection: "column",
          width: "400px",
          cursor: "pointer",
        })}
        onClick={handleOpenForm}
      >
        {/* Header */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mr: -1 }}
        >
          <Box display="flex" alignItems="center">
            {icon}
            <Typography
              variant="label1"
              color="grey.80"
              sx={(theme) => ({
                textTransform: "uppercase",
                color: theme.palette.purple[150],
              })}
            >
              ACTION {"›"} {data.label}
            </Typography>
          </Box>

          <MenuIconButton
            icon={<MoreHorizontal />}
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem onClick={handleOpenForm}>
              {viewMode ? "Preview" : "Edit"}
            </MenuItem>
            {!viewMode && <MenuItem onClick={handleDelete}>Delete</MenuItem>}
          </MenuIconButton>
        </Box>

        <Box>
          <TruncateTooltip arrow>
            <Typography
              variant="p1"
              color="grey.100"
              fontWeight="600"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {data.name || data.audienceName || data.tagNames}
            </Typography>
          </TruncateTooltip>
        </Box>

        {/* Config chip */}
        <Box mt={2}>
          {configured ? (
            <Chip
              sx={(theme) => ({
                height: theme.spacing(5),
                backgroundColor: theme.palette.primary[100],
                color: theme.palette.common.white,
                mr: 1,
                fontSize: theme.spacing(3),
                "& .MuiChip-label": { fontWeight: 700 },
              })}
              label="Configured"
            />
          ) : (
            <Chip
              sx={(theme) => ({
                height: theme.spacing(5),
                backgroundColor: theme.palette.yellow[150],
                color: theme.palette.common.white,
                mr: 1,
                fontSize: theme.spacing(3),
                "& .MuiChip-label": { fontWeight: 700 },
              })}
              label="Not-Configured"
            />
          )}
        </Box>

        {data.label === "Email" && configured && showNodeStats && (
          <>
            <Divider sx={{ mt: 4 }} />
            <Box mt={2}>{renderStatsContent()}</Box>
          </>
        )}
      </Box>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: "transparent", border: "none" }}
      />
    </>
  );
};

export default ActionNode;
